import React, { createContext, useEffect, useRef, useState } from "react";
import { createAuth0Client } from "@auth0/auth0-spa-js";
import { auth0Config, validateAuth0Config } from "../../utils/auth0-config";
import { PUBLIC_CAREERS_URL } from "../../utils/constants";

// Create Auth0 context
export const Auth0Context = createContext();

/**
 * Auth0Provider component that wraps the application with Auth0 authentication
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const Auth0Provider = ({ children }) => {
  const [auth0Client, setAuth0Client] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  // React 18 StrictMode mounts effects twice in development. Auth0's
  // handleRedirectCallback() consumes the one-time login transaction (state +
  // code_verifier) from storage, so a second call always fails with
  // "Invalid state" / missing_transaction. Initialise exactly once.
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const initAuth0 = async () => {
      // Held rather than surfaced immediately - a spent transaction on a
      // session that is otherwise valid is not worth showing the user.
      let redirectCallbackError = null;

      try {
        // Validate configuration before initializing
        validateAuth0Config();

        // Create Auth0 client
        const client = await createAuth0Client(auth0Config);
        setAuth0Client(client);

        // Check if user is authenticated (handles redirect callback)
        if (
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          // handleRedirectCallback() hands back the appState stashed at
          // loginWithRedirect() time; ProtectedRoute puts the path the user
          // was trying to reach in appState.returnTo. Held out here so the
          // finally block can restore it.
          let returnTo = null;

          try {
            const { appState } = (await client.handleRedirectCallback()) || {};
            returnTo = appState?.returnTo || null;
          } catch (callbackError) {
            redirectCallbackError = callbackError;
            console.error(
              "❌ Error handling redirect callback:",
              callbackError
            );
            console.error("Callback error details:", {
              name: callbackError.name,
              message: callbackError.message,
              stack: callbackError.stack,
              error: callbackError.error,
              error_description: callbackError.error_description,
            });
          } finally {
            // Clean up the URL even on failure, so that a reload cannot
            // replay an already-spent code/state pair. Restoring returnTo in
            // the same call keeps this to one history entry; it is done here
            // rather than with a router navigate because this provider is
            // mounted outside the Router (see index.js) and has no router
            // context. On a failed callback returnTo is null and this is the
            // plain strip it always was.
            window.history.replaceState(
              {},
              document.title,
              returnTo || window.location.pathname
            );
          }
        }

        // Check authentication status
        const authenticated = await client.isAuthenticated();
        setIsAuthenticated(authenticated);

        // Get user info if authenticated
        if (authenticated) {
          const userData = await client.getUser();
          setUser(userData);
        } else if (redirectCallbackError) {
          // Only surface the callback failure if it actually left the user
          // signed out.
          setError(
            redirectCallbackError.message ||
              redirectCallbackError.error_description ||
              "Authentication callback failed"
          );
        }
      } catch (initError) {
        console.error("❌ Error initializing Auth0:", initError);
        console.error("Init error details:", {
          name: initError.name,
          message: initError.message,
          stack: initError.stack,
        });
        setError(initError.message);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth0();
  }, []);

  /**
   * Login with redirect
   */
  const loginWithRedirect = async (options = {}) => {
    try {
      if (auth0Client) {
        await auth0Client.loginWithRedirect(options);
      } else {
        console.error("❌ Auth0 client not available for login");
        setError("Auth0 client not initialized");
      }
    } catch (loginError) {
      console.error("❌ Error during login:", loginError);
      console.error("Login error details:", {
        name: loginError.name,
        message: loginError.message,
        stack: loginError.stack,
      });
      setError(loginError.message);
    }
  };

  /**
   * Logout
   *
   * Returns to the public Webflow careers page rather than this app's origin -
   * "/" is now the protected internal job board, so returning there would drop
   * the user straight onto the "Access Restricted" gate.
   */
  const logout = (options = {}) => {
    if (auth0Client) {
      auth0Client.logout({
        logoutParams: {
          returnTo: PUBLIC_CAREERS_URL,
        },
        ...options,
      });
    }
  };

  /**
   * Get access token
   */
  const getAccessTokenSilently = async (options = {}) => {
    try {
      if (auth0Client) {
        return await auth0Client.getAccessTokenSilently(options);
      }
      return null;
    } catch (tokenError) {
      console.error("Error getting access token:", tokenError);
      throw tokenError;
    }
  };

  // Context value
  const contextValue = {
    isLoading,
    isAuthenticated,
    user,
    error,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  };

  return (
    <Auth0Context.Provider value={contextValue}>
      {children}
    </Auth0Context.Provider>
  );
};
